import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, IconButton } from '@material-ui/core';
import { PiVideoCamera, PiXCircle, PiArrowsOutSimple } from 'react-icons/pi';
import { useExperience } from '../contexts/experience';
import { useTheme } from '../contexts/theme';
import SingleVideoPlayer from './SingleVideoPlayer';
import { useLanguage } from '../contexts/language';

// Movement (px) past which a pointerdown becomes a drag instead of a tap: below
// it, no pointer capture is taken, so taps on the expand button and on the
// video's native controls keep working (capture would retarget their click).
const DRAG_THRESHOLD_PX = 5;

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

// Drag limits for the mini player: it can leave the viewport by at most half
// its width horizontally and half its height vertically, so a grabbable part
// always stays on screen (per-axis: a corner drag leaves a quarter visible).
const getDragBounds = (rect, base) => ({
  minDx: base.dx - (rect.left + rect.width / 2),
  maxDx: base.dx + (window.innerWidth + rect.width / 2 - rect.right),
  minDy: base.dy - (rect.top + rect.height / 2),
  maxDy: base.dy + (window.innerHeight + rect.height / 2 - rect.bottom),
});

const clampToBounds = (offset, bounds) => ({
  dx: clampValue(offset.dx, bounds.minDx, bounds.maxDx),
  dy: clampValue(offset.dy, bounds.minDy, bounds.maxDy),
});

const VideoPlayerOnMap = () => {
  const { isVideoActive, videoActive, videoToBeActivated, geoXpVideoEvents } = useExperience();
  // 'closed' → hidden (playback torn down), 'expanded' → fullscreen,
  // 'pip' → draggable mini player over the map
  const [view, setView] = React.useState('closed');
  const [pipOffset, setPipOffset] = React.useState({ dx: 0, dy: 0 });
  const { content } = useLanguage();

  const videoSeeksMapRef = React.useRef(new Map());
  React.useEffect(
    () => videoActive.forEach((video) => videoSeeksMapRef?.current?.set(video.id, 0)),
    [videoActive]
  );

  const hasVideoToBeActivated = React.useMemo(
    () => videoToBeActivated && videoToBeActivated.length > 0,
    [videoToBeActivated]
  );

  const singleVideo = videoToBeActivated?.length === 1;

  // collapse-to-pip is only offered when every active video is geo-located;
  // manual videos (and multi-video sets) keep the plain close-to-stop behaviour.
  const allGeo = React.useMemo(
    () => hasVideoToBeActivated && videoToBeActivated.every((video) => !!video.position),
    [hasVideoToBeActivated, videoToBeActivated]
  );

  const { styles } = useTheme();
  const backgroundColor = styles.palette.background.default;
  const primary = styles.palette.primary.main;

  // auto-open the fullscreen player only when a video first becomes active
  // (rising edge), so exiting the mini player doesn't bounce it back open
  const hadVideoRef = React.useRef(false);
  React.useEffect(() => {
    if (hasVideoToBeActivated && !hadVideoRef.current) setView('expanded');
    hadVideoRef.current = hasVideoToBeActivated;
  }, [hasVideoToBeActivated]);

  // tear everything down once no video is active/pending anymore (geofence exit)
  React.useEffect(() => {
    if (!hasVideoToBeActivated && !isVideoActive) setView('closed');
  }, [hasVideoToBeActivated, isVideoActive]);

  // X on the player.
  // - single geo video: collapse to the draggable mini player over the map;
  // - multiple geo videos: close to the reopen bar (playback kept, geo UX untouched);
  // - manual videos (code / QR): end playback entirely, mirroring the stop button.
  const handleClose = React.useCallback(() => {
    if (allGeo) {
      if (singleVideo) {
        setPipOffset({ dx: 0, dy: 0 });
        setView('pip');
      } else {
        setView('closed');
      }
      return;
    }
    videoToBeActivated.forEach((video) => {
      videoSeeksMapRef?.current?.set(video.id, 0);
      geoXpVideoEvents.videoEnded(video.spotId);
    });
  }, [allGeo, singleVideo, videoToBeActivated, geoXpVideoEvents]);

  const restore = React.useCallback(() => {
    setPipOffset({ dx: 0, dy: 0 });
    setView('expanded');
  }, []);

  // Drag for the mini player, whole surface. Handlers live on the container
  // (no window listeners to leak on unmount): before the movement threshold,
  // events flow normally so taps keep working; past it, pointer capture keeps
  // move/up/cancel coming to the container even outside it, and the browser
  // releases the capture itself on gesture end or element removal.
  const dragRef = React.useRef(null);
  const pipBoxRef = React.useRef(null);

  const startDrag = React.useCallback(
    (event) => {
      if (dragRef.current) return; // first active pointer wins
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        base: pipOffset,
        bounds: getDragBounds(event.currentTarget.getBoundingClientRect(), pipOffset),
        dragging: false,
      };
    },
    [pipOffset]
  );

  const moveDrag = React.useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      drag.dragging = true;
      event.currentTarget.setPointerCapture(drag.pointerId);
    }
    setPipOffset(clampToBounds({ dx: drag.base.dx + dx, dy: drag.base.dy + dy }, drag.bounds));
  }, []);

  const endDrag = React.useCallback((event) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  }, []);

  // rotating/resizing can strand a clamped offset outside the new viewport:
  // re-clamp the current offset against the new geometry
  React.useEffect(() => {
    if (view !== 'pip') return undefined;
    const onResize = () => {
      const el = pipBoxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPipOffset((current) => clampToBounds(current, getDragBounds(rect, current)));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [view]);

  const getLabelText = React.useCallback(() => {
    if (!videoActive?.length) return null;
    return singleVideo ? videoActive[0]?.label || '' : content('multiple_agami_active');
  }, [videoActive, singleVideo, content]);

  const labelText = React.useMemo(() => getLabelText(), [getLabelText]);

  const mounted = view !== 'closed' && hasVideoToBeActivated;
  const showReopenBar = isVideoActive && view === 'closed';
  const isPip = view === 'pip';

  const containerStyle = isPip
    ? {
        position: 'fixed',
        right: 8,
        bottom: 88,
        width: 240,
        maxWidth: '70vw',
        zIndex: 1300,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
        backgroundColor: '#000',
        transform: `translate(${pipOffset.dx}px, ${pipOffset.dy}px)`,
        touchAction: 'none',
        cursor: 'move',
      }
    : { position: 'fixed', inset: 0, zIndex: 1300, backgroundColor };

  return (
    <>
      {/* Reopen bar: shown when a video is still active but the player is closed */}
      <Box
        position="absolute"
        width="100%"
        bgcolor={primary}
        bottom={0}
        borderRadius="5px 5px 0 0"
        zIndex={999}
        aria-hidden={!showReopenBar}
        style={{
          opacity: showReopenBar ? 1 : 0,
          pointerEvents: showReopenBar ? 'all' : 'none',
          transform: showReopenBar ? 'scale(1) translate(0px, 0px)' : 'scale(0.8) translate(0px, 100px)',
          transition: 'all .5s ease-in-out',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          fullWidth
          onClick={restore}
          aria-label={content('aria_open_video_player')}
          tabIndex={showReopenBar ? 0 : -1}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }}
        >
          <span
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8 }}
          >
            <PiVideoCamera style={{ fontSize: '1.5rem' }} aria-hidden="true" />
            {labelText && <span aria-live="polite">{labelText}</span>}
          </span>
        </Button>
      </Box>

      {mounted &&
        createPortal(
          <Box
            ref={pipBoxRef}
            role={isPip ? 'region' : 'dialog'}
            aria-modal={isPip ? undefined : true}
            aria-label={isPip ? content('aria_collapsed_video_player') : content('aria_active_video_dialog')}
            onPointerDown={isPip ? startDrag : undefined}
            onPointerMove={isPip ? moveDrag : undefined}
            onPointerUp={isPip ? endDrag : undefined}
            onPointerCancel={isPip ? endDrag : undefined}
            style={containerStyle}
          >
            {/* Fullscreen: single close (X) control */}
            {!isPip && (
              <Box position="absolute" right={4} top={4} zIndex={2}>
                <IconButton
                  onClick={handleClose}
                  // same condition as handleClose: for a single geo video the X
                  // collapses to PIP, so the announced action must say so
                  aria-label={content(
                    allGeo && singleVideo ? 'aria_collapse_video_player' : 'aria_close_video_player'
                  )}
                >
                  <PiXCircle aria-hidden="true" />
                </IconButton>
              </Box>
            )}

            {/* Mini player: exit/restore control over the video */}
            {isPip && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                zIndex={2}
                display="flex"
                justifyContent="flex-end"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))' }}
              >
                <IconButton
                  size="small"
                  onClick={restore}
                  aria-label={content('aria_expand_video_player')}
                  style={{ color: '#fff' }}
                >
                  <PiArrowsOutSimple aria-hidden="true" />
                </IconButton>
              </Box>
            )}

            {/* Stable player subtree — same in both states, only the styles above change */}
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height={isPip ? 'auto' : '100%'}
              padding={isPip ? 0 : 2}
              style={{ columnGap: isPip ? 0 : 16 }}
            >
              {videoToBeActivated.map((video) => (
                <Box key={video.spotId} position="relative" textAlign="center" width="100%">
                  <SingleVideoPlayer video={video} videoSeeksMapRef={videoSeeksMapRef} compact={isPip} />
                </Box>
              ))}
            </Box>
          </Box>,
          document.body
        )}
    </>
  );
};

export default VideoPlayerOnMap;
